/**
 * Öffentliche 1-Klick-Werbe-Abmeldung (§ 7 UWG).
 * Kunde öffnet /abmelden/:token und bestätigt den Widerruf mit einem Klick.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { MailX, CheckCircle2, Loader2, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AbmeldenPage() {
    const { token } = useParams();
    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API}/public/unsubscribe/${token}`);
                setInfo(data);
                if (data.already_unsubscribed) setDone(true);
            } catch (e) {
                setError(e?.response?.data?.detail || "Abmeldelink ungültig oder abgelaufen.");
            } finally { setLoading(false); }
        })();
    }, [token]);

    const confirm = async () => {
        setSaving(true);
        try {
            await axios.post(`${API}/public/unsubscribe/${token}`);
            setDone(true);
        } catch (e) {
            setError(e?.response?.data?.detail || "Abmeldung fehlgeschlagen.");
        } finally { setSaving(false); }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-testid="unsubscribe-page">
            <div className="bg-white border border-gray-200 rounded-sm shadow-sm w-full max-w-md p-7">
                <div className="flex items-center gap-2 mb-4">
                    <MailX className="w-6 h-6 text-red-600" />
                    <h1 className="font-bold text-lg">Werbung abbestellen</h1>
                </div>

                {loading ? (
                    <div className="flex items-center gap-2 text-gray-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Lädt…</div>
                ) : error ? (
                    <div className="flex items-center gap-2 text-red-600 text-sm" data-testid="unsubscribe-error"><XCircle className="w-5 h-5" /> {error}</div>
                ) : done ? (
                    <div className="text-center py-6" data-testid="unsubscribe-done">
                        <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                        <p className="font-bold">Sie wurden abgemeldet.</p>
                        <p className="text-sm text-gray-500 mt-1">
                            Sie erhalten künftig keine Werbe-E-Mails von <b>{info?.dealer}</b> mehr.
                        </p>
                    </div>
                ) : (
                    <>
                        <p className="text-sm text-gray-600 mb-5">
                            Möchten Sie keine Werbung mehr von <b>{info?.dealer}</b> erhalten?
                            Mit einem Klick können Sie sich von allen Werbe-Benachrichtigungen abmelden.
                        </p>
                        <button onClick={confirm} disabled={saving}
                            className="w-full bg-red-600 hover:bg-red-700 text-white font-bold rounded-sm py-2.5 flex items-center justify-center gap-2 disabled:opacity-50"
                            data-testid="unsubscribe-confirm">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <MailX className="w-4 h-4" />} Jetzt abmelden
                        </button>
                        <p className="text-xs text-gray-400 mt-3 text-center">
                            Transaktionsmails (z. B. Rechnungen, Terminbestätigungen) sind davon nicht betroffen.
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}
